import { Interpreter } from 'xstate-v4'

/**
 * Wait for an XState machine to reach a success or error state
 *
 * @param machine - XState interpreter instance to monitor
 * @param successStates - Array of state names that indicate successful completion
 * @param errorStates - Optional array of state names that indicate error conditions
 * @returns Promise that resolves with the machine state when a success state is reached
 * @throws {Error} Always throws proper Error objects (never primitives) when error state is reached
 *
 * @example
 * ```typescript
 * try {
 *   const state = await waitForInterpreterState({
 *     machine: myMachine,
 *     successStates: ['completed', 'ready'],
 *     errorStates: ['failed', 'error']
 *   })
 *   console.log('Success:', state.context)
 * } catch (error) {
 *   // error is always an Error instance with stack trace
 *   console.error('Machine failed:', error.message)
 * }
 * ```
 */
export function waitForInterpreterState<T extends Interpreter<any, any, any>>({
  machine,
  errorStates,
  successStates,
}: {
  machine: T
  errorStates?: string[]
  successStates: string[]
}): Promise<T['state']> {
  return new Promise((resolve, reject) => {
    const isSuccessState = (state: T['state']) =>
      successStates.findIndex((successState) => state.matches(successState)) >
      -1
    const isErrorState = (state: T['state']) =>
      errorStates &&
      errorStates.findIndex((errorState) => state.matches(errorState)) > -1

    const machineListener = (state: T['state']) => {
      if (isSuccessState(state)) {
        machine.off(machineListener)
        resolve(state)
      } else if (isErrorState(state)) {
        machine.off(machineListener)
        const error = state?.context?.error
        reject(
          error instanceof Error
            ? error
            : new Error(
                `XState machine entered error state: ${JSON.stringify(state.value)} - ${String(error || 'Unknown error')}`,
              ),
        )
      }
    }
    machineListener(machine.state)
    machine.onTransition(machineListener)
  })
}
